import {
  logout,
  readAccessToken,
  readRefreshToken,
  writeAccessToken,
  writeRefreshToken,
} from "./functions/authFunctions";
import { ApiError } from "./types/apiError";
import { showAlert } from "@/components/containers/Alert";
import axios, { AxiosError, AxiosRequestConfig } from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_HOST,
  // baseURL: "http://172.30.1.25:3001",
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use(
  async (config) => {
    const accessToken = readAccessToken();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const jwtToken = readAccessToken();
      const refreshToken = readRefreshToken();
      if (!refreshToken) {
        showAlert("로그인해주세요");
        return logout();
      }
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_API_HOST}/api/Users/refresh-token`,
          {
            jwtToken,
            refreshToken,
          },
          {
            headers: {
              Authorization: `Bearer ${jwtToken}`,
            },
          }
        );

        const accessTokenData = response?.data?.jwtToken;
        const refreshTokenData = response?.data?.refreshToken;
        writeAccessToken(accessTokenData);
        writeRefreshToken(refreshTokenData);

        return api(originalRequest);
      } catch (e) {
        showAlert("토큰이 만료되었습니다. 다시 로그인해 주세요.");
        return logout();
      }
    }

    return Promise.reject(error);
  }
);

// interface CustomAxiosRequestConfig extends AxiosRequestConfig {
//   skipAuthHandling?: boolean;
// }

// api.interceptors.request.use(
//   async (config) => {
//     // skipAuthHandling이 true인 경우 인증 헤더를 추가하지 않음
//     if (!(config as any).skipAuthHandling) {
//       const accessToken = readAccessToken();
//       if (accessToken) {
//         config.headers.Authorization = `Bearer ${accessToken}`;
//       }
//     }
//     return config;
//   },
//   (error) => Promise.reject(error)
// );

// api.interceptors.response.use(
//   (response) => response,
//   async (error) => {
//     const originalRequest = error.config;

//     // skipAuthHandling인 경우 인증 실패 시 아무 처리도 하지 않고 에러 반환
//     if (originalRequest?.skipAuthHandling) {
//       return Promise.reject(error);
//     }

//     if (error.response?.status === 401 && !originalRequest._retry) {
//       originalRequest._retry = true;
//       const jwtToken = readAccessToken();
//       const refreshToken = readRefreshToken();
//       if (!refreshToken) {
//         showAlert("로그인해주세요");
//         return logout();
//       }

//       try {
//         const response = await axios.post(
//           `${process.env.REACT_APP_API_HOST}/api/Users/refresh-token`,
//           {
//             jwtToken,
//             refreshToken,
//           },
//           {
//             headers: {
//               Authorization: `Bearer ${jwtToken}`,
//             },
//           }
//         );

//         const accessTokenData = response?.data?.jwtToken;
//         const refreshTokenData = response?.data?.refreshToken;
//         writeAccessToken(accessTokenData);
//         writeRefreshToken(refreshTokenData);

//         return api(originalRequest);
//       } catch (e) {
//         showAlert("토큰이 만료되었습니다. 다시 로그인해 주세요.");
//         return logout();
//       }
//     }

//     return Promise.reject(error);
//   }
// );

/**
 * @param config - axios request config
 * @param isShowError - 에러 발생시 alert 띄울지 여부
 * @returns
 */
const request = async <T>(
  config: AxiosRequestConfig,
  // config: CustomAxiosRequestConfig,
  isShowError: boolean = true
): Promise<T> => {
  const { method } = config;
  const isGetRequest = method?.toUpperCase() === "GET";

  try {
    const { data } = await api.request<T>({ ...config });

    return data;
  } catch (error) {
    let message = "서버요청 에러!";

    if (error instanceof AxiosError) {
      const { response }: any = error;
      if (response?.data?.message) {
        message = response.data.message;
      }

      if (isGetRequest) {
        if (error?.response?.status === 403) {
          showAlert("조회 권한이 없습니다.");
        }
        return [] as any;
      }
    } else if (error instanceof ApiError) {
      message = error.message;
    }

    if (isShowError && !isGetRequest) {
      showAlert(message);
    }

    throw error;
  }
};

export { request };
